/*
 * Elexa A3: product trust blocks (loaded deferred, after assets/elexa-core.js).
 *
 *   <elexa-ship-date>        shipping notice date (Elexa.delivery.estimate, store time, refreshes
 *                            every minute and at the cut-off)
 *   <elexa-review-carousel>  customer review carousel on top of the foundation <elexa-carousel>:
 *                            middle start slide, dots, wrap-around, mouse/touch drag with a 50px
 *                            threshold, arrow keys, active card state
 *   [data-ex-smooth-anchor]  rating summary link: smooth scroll to an on-page anchor
 *
 * Custom elements re-initialise by themselves when the theme editor re-renders a section (C7).
 */
(() => {
  if (window.Elexa && window.Elexa.pbTrustLoaded) return;
  const Elexa = (window.Elexa = window.Elexa || {});
  Elexa.pbTrustLoaded = true;

  const config = () => Elexa.config || {};
  const reducedMotion = () =>
    Elexa.reducedMotion ? Elexa.reducedMotion() : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isRTL = (el) => getComputedStyle(el).direction === 'rtl';
  const pad2 = (n) => String(n).padStart(2, '0');
  const DAY = 86400000;

  /* ------------------------------------------------------------------ *
   * Shipping notice date
   * ------------------------------------------------------------------ */
  const ordinal = (d) => {
    if (d >= 11 && d <= 13) return 'th';
    if (d % 10 === 1) return 'st';
    if (d % 10 === 2) return 'nd';
    if (d % 10 === 3) return 'rd';
    return 'th';
  };

  /** Format a store-calendar date (UTC fields) for the shipping notice. */
  function formatShipDate(date, format, withOrdinal) {
    const locale = config().locale || document.documentElement.lang || 'en';
    const lang = String(locale).slice(0, 2).toLowerCase();
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    if (format === 'dd_mm') return `${pad2(day)}/${pad2(month)}`;
    if (format === 'mm_dd') return `${pad2(month)}/${pad2(day)}`;
    let useOrdinal = withOrdinal && lang === 'en';
    if (format === 'theme') {
      const fmt = (config().settings || {}).shipNoticeDateFormat;
      if (fmt && Elexa.delivery && Elexa.delivery.strftime) return Elexa.delivery.strftime(date, fmt, locale);
      format = 'month_day';
      useOrdinal = lang === 'en';
    }
    const opts = { timeZone: 'UTC', month: 'long', day: 'numeric' };
    if (format === 'weekday_month_day') opts.weekday = 'long';
    let parts;
    try {
      parts = new Intl.DateTimeFormat(locale, opts).formatToParts(date);
    } catch (e) {
      parts = new Intl.DateTimeFormat('en', opts).formatToParts(date);
    }
    return parts.map((p) => (p.type === 'day' && useOrdinal ? p.value + ordinal(day) : p.value)).join('');
  }

  class ElexaShipDate extends HTMLElement {
    connectedCallback() {
      this.update();
      this.interval = setInterval(() => this.update(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      clearTimeout(this.cutoffTimer);
    }

    update() {
      if (!Elexa.delivery || !Elexa.delivery.estimate) return;
      const ds = this.dataset;
      const days = Math.max(0, parseInt(ds.days || '2', 10) || 0);
      const business = ds.business === 'true';
      const opts = {
        cutoff: ds.cutoff || '24:00',
        processingDays: 0,
        minDays: days,
        maxDays: days,
        dispatchSaturday: !business,
        dispatchSunday: !business,
        deliverSaturday: !business,
        deliverSunday: !business,
      };
      if (!business) opts.holidays = [];
      const est = Elexa.delivery.estimate(opts);
      const text = formatShipDate(est.earliest, ds.format || 'month_day', ds.ordinal === 'true');
      if (this.textContent !== text) this.textContent = text;
      clearTimeout(this.cutoffTimer);
      if (est.cutoffAt) {
        const wait = est.cutoffAt - Date.now() + 500;
        if (wait > 0 && wait < 2 * DAY) this.cutoffTimer = setTimeout(() => this.update(), wait);
      }
    }
  }
  if (!customElements.get('elexa-ship-date')) customElements.define('elexa-ship-date', ElexaShipDate);

  /* ------------------------------------------------------------------ *
   * Customer review carousel
   * ------------------------------------------------------------------ */
  const DRAG_THRESHOLD = 50;

  class ElexaReviewCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.carousel = this.querySelector('elexa-carousel');
      this.track = this.querySelector('[data-ex-track]');
      if (!this.track) return;
      this.initialised = true;
      this.dots = Array.from(this.querySelectorAll('[data-ex-rc-dot]'));
      this.index = parseInt(this.dataset.start || '0', 10) || 0;

      this.onDot = (e) => {
        const dot = e.target.closest('[data-ex-rc-dot]');
        if (dot && this.contains(dot)) this.go(parseInt(dot.dataset.exRcDot, 10));
      };
      this.onChange = (e) => {
        if (e.target !== this.carousel || this.drag) return;
        this.setActive(e.detail.index);
      };
      this.onKey = (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const forward = (e.key === 'ArrowRight') !== isRTL(this.track);
        this.go(this.index + (forward ? 1 : -1));
      };
      this.onDown = this.pointerDown.bind(this);
      this.onMove = this.pointerMove.bind(this);
      this.onUp = this.pointerUp.bind(this);
      this.onCancel = this.pointerCancel.bind(this);
      this.onDragStart = (e) => e.preventDefault();

      this.addEventListener('click', this.onDot);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('elexa:carousel-change', this.onChange);
      this.track.addEventListener('pointerdown', this.onDown);
      this.track.addEventListener('pointermove', this.onMove);
      this.track.addEventListener('pointerup', this.onUp);
      this.track.addEventListener('pointercancel', this.onCancel);
      this.track.addEventListener('lostpointercapture', this.onCancel);
      this.track.addEventListener('dragstart', this.onDragStart);

      this.jump(this.index);
      this.setActive(this.index);
      // The inner <elexa-carousel> may upgrade after this element (section re-render): settle once.
      requestAnimationFrame(() => this.jump(this.index));
    }

    disconnectedCallback() {
      if (!this.initialised) return;
      this.removeEventListener('click', this.onDot);
      this.removeEventListener('keydown', this.onKey);
      this.removeEventListener('elexa:carousel-change', this.onChange);
      if (this.track) {
        this.track.removeEventListener('pointerdown', this.onDown);
        this.track.removeEventListener('pointermove', this.onMove);
        this.track.removeEventListener('pointerup', this.onUp);
        this.track.removeEventListener('pointercancel', this.onCancel);
        this.track.removeEventListener('lostpointercapture', this.onCancel);
        this.track.removeEventListener('dragstart', this.onDragStart);
      }
      clearTimeout(this.snapTimer);
      this.initialised = false;
    }

    slides() {
      return Array.from(this.track.children).filter((el) => !el.hidden && el.getClientRects().length);
    }

    offsetOf(i) {
      const slides = this.slides();
      if (!slides[i] || !slides[0]) return 0;
      return Math.abs(slides[i].offsetLeft - slides[0].offsetLeft);
    }

    scrollPos() {
      return Math.abs(this.track.scrollLeft);
    }

    setScroll(x, smooth) {
      const left = isRTL(this.track) ? -x : x;
      this.track.scrollTo({ left, behavior: smooth && !reducedMotion() ? 'smooth' : 'instant' });
    }

    jump(i) {
      this.track.style.scrollBehavior = 'auto';
      this.setScroll(this.offsetOf(i), false);
      this.track.style.scrollBehavior = '';
    }

    /** Go to slide i, wrapping past either end (animates straight across). */
    go(i, instant = false) {
      const total = this.slides().length;
      if (!total) return;
      const index = ((i % total) + total) % total;
      this.setActive(index);
      this.setScroll(this.offsetOf(index), !instant);
    }

    setActive(index) {
      const slides = this.slides();
      if (!slides.length) return;
      this.index = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
      this.dots.forEach((d, n) => d.setAttribute('aria-current', n === this.index ? 'true' : 'false'));
    }

    /* Drag / swipe: the carousel takes over once horizontal movement wins; vertical page
       scrolling stays native (touch-action: pan-y on the track). */
    pointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (this.slides().length < 2) return;
      clearTimeout(this.snapTimer);
      this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, start: this.scrollPos(), active: false };
    }

    pointerMove(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      if (!d.active) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dx) <= Math.abs(dy)) {
          this.drag = null;
          return;
        }
        d.active = true;
        try {
          this.track.setPointerCapture(e.pointerId);
        } catch (err) {
          /* pointer already released */
        }
        this.classList.add('is-dragging');
      }
      e.preventDefault();
      const dir = isRTL(this.track) ? -1 : 1;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const x = Math.max(0, Math.min(max, d.start - dx * dir));
      this.track.scrollLeft = dir === -1 ? -x : x;
    }

    pointerUp(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      this.drag = null;
      if (!d.active) return;
      const dx = (e.clientX - d.x) * (isRTL(this.track) ? -1 : 1);
      let target = this.index;
      if (dx < -DRAG_THRESHOLD) target = this.index + 1;
      else if (dx > DRAG_THRESHOLD) target = this.index - 1;
      this.release(target);
    }

    pointerCancel(e) {
      const d = this.drag;
      if (!d || (e.pointerId !== undefined && e.pointerId !== d.id)) return;
      // Touch pointers are implicitly captured by the touched card; moving the capture to the
      // track fires lostpointercapture on that card, which is not a cancel.
      if (e.type === 'lostpointercapture' && e.target !== this.track) return;
      this.drag = null;
      if (d.active) this.release(this.index);
    }

    /** Animate to the target with snapping off, then restore the native snap. */
    release(target) {
      clearTimeout(this.snapTimer);
      const done = () => {
        clearTimeout(this.snapTimer);
        this.track.removeEventListener('scrollend', done);
        if (!this.drag) this.classList.remove('is-dragging');
      };
      if (!reducedMotion() && 'onscrollend' in window) this.track.addEventListener('scrollend', done, { once: true });
      this.go(target);
      this.snapTimer = setTimeout(done, reducedMotion() ? 0 : 1000);
    }
  }
  if (!customElements.get('elexa-review-carousel')) customElements.define('elexa-review-carousel', ElexaReviewCarousel);

  /* ------------------------------------------------------------------ *
   * Rating summary: smooth scroll to an on-page anchor
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('a[data-ex-smooth-anchor]');
    if (!link || !link.hash) return;
    let samePage = link.getAttribute('href').charAt(0) === '#';
    try {
      const url = new URL(link.href, window.location.href);
      samePage = samePage || (url.origin === window.location.origin && url.pathname === window.location.pathname);
    } catch (err) {
      /* ignore malformed URLs */
    }
    if (!samePage) return;
    let id = link.hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (err) {
      /* keep the raw id */
    }
    const target = id && document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    if (Elexa.scrollTo) Elexa.scrollTo(target);
    else target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth' });
    if (window.history && history.replaceState) history.replaceState(null, '', `#${encodeURIComponent(id)}`);
  });
})();
